/** A validated local calendar-date string. It deliberately contains no time or UTC offset. */
export type CalendarDate = string;

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function calendarDate(value: string): CalendarDate {
  const match = CALENDAR_DATE_PATTERN.exec(value);
  if (!match) throw new Error(`Invalid calendar date: ${value}`);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    throw new Error(`Invalid calendar date: ${value}`);
  }
  return value;
}

export function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  if (!Number.isInteger(days)) throw new Error("Calendar day offset must be an integer");
  const instant = Date.parse(`${date}T00:00:00.000Z`);
  return new Date(instant + days * 86_400_000).toISOString().slice(0, 10);
}

export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  return Math.round((Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000);
}

export function compareCalendarDates(a: CalendarDate, b: CalendarDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function isDateWithin(date: CalendarDate, start: CalendarDate, end: CalendarDate): boolean {
  return compareCalendarDates(date, start) >= 0 && compareCalendarDates(date, end) <= 0;
}

export function calendarDateRange(start: CalendarDate, end: CalendarDate): CalendarDate[] {
  const count = daysBetween(start, end);
  if (count < 0) return [];
  return Array.from({ length: count + 1 }, (_, index) => addCalendarDays(start, index));
}
